import { useShallow } from "zustand/react/shallow";

import Button, { ButtonProps } from "ds/components/Button";
import useIntelligenceSettings from "shared/Intelligence/useIntelligenceSettings";

import { AssistantPanelConversationEntryPoint } from "../analytics/types";
import useAssistantPanelAnalytics from "../analytics/useAnalytics";
import useAssistantPanelSendPrompt from "../hooks/useSendPrompt";
import { AssistantPanelView } from "../Layout/types";
import useAssistantPanelStore from "../Layout/useStore";

type AssistantPanelAskButtonProps = Omit<ButtonProps, "variant" | "children" | "onPress"> & {
  entryPoint: AssistantPanelConversationEntryPoint;
  /** Prefilled in the composer, not sent. */
  prompt?: string;
  alwaysVisible?: boolean;
};

const AssistantPanelAskButton = ({
  entryPoint,
  prompt,
  alwaysVisible,
  ...buttonProps
}: AssistantPanelAskButtonProps) => {
  const { canUseInfraAssistant } = useIntelligenceSettings();
  const { openPrompt, isChatOpen } = useAssistantPanelSendPrompt();
  const { setIsOpen, switchView } = useAssistantPanelStore(
    useShallow((state) => ({
      setIsOpen: state.setIsOpen,
      switchView: state.switchView,
    }))
  );
  const { trackConversationOpened } = useAssistantPanelAnalytics();

  if (!canUseInfraAssistant && !alwaysVisible) {
    return null;
  }

  const handlePress = () => {
    trackConversationOpened(entryPoint);

    if (prompt) {
      openPrompt(prompt, { newChat: !isChatOpen });
      return;
    }

    switchView(AssistantPanelView.CHAT);
    setIsOpen(true);
  };

  return (
    <Button {...buttonProps} variant="ai" onPress={handlePress}>
      Ask
    </Button>
  );
};

export default AssistantPanelAskButton;
